import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateNrtRecordRequest,
  DeleteNrtRecordRequest,
  NrtRecord,
  NrtRecordWire,
  NrtRecordsResponseBody,
  UpdateNrtRecordRequest
} from "@dealer-center/ddc-pages-nrt-database-filter-tool-interface";

// Same-origin base every handler in this Lambda is mounted under.
export const NRT_LAMBDA_BASE_PATH = "/invsup/nxlambda/nrtDatabaseFilterTool";

// Every list consumer (the query itself, plus the create/update mutations
// that need to invalidate it) shares this key, so it only lives in one place.
export const NRT_RECORDS_LIST_QUERY_KEY = [
  "ddc-pages-nrt-database-filter-tool",
  "list"
] as const;

const asText = (value: unknown): string | null =>
  value === null || value === undefined ? null : String(value);

// The one place wire data becomes a trustworthy NrtRecord: getAllRecords casts
// rather than converts, so an N-typed attribute arrives as a number.
export const normalizeNrtRecord = (item: NrtRecordWire): NrtRecord => ({
  accountId: String(item.accountId),
  id: String(item.id),
  caseNumber: String(item.caseNumber),
  classification: (item.classification ?? null) as NrtRecord["classification"],
  createdBy: String(item.createdBy),
  dateCreated: asText(item.dateCreated),
  expirationDate: asText(item.expirationDate),
  lastAccessed: asText(item.lastAccessed),
  map: asText(item.map),
  notes: asText(item.notes),
  providerId: String(item.providerId),
  select: asText(item.select),
  status: item.status as NrtRecord["status"],
  tags: asText(item.tags),
  type: (item.type ?? null) as NrtRecord["type"]
});

// API Gateway caps limit at 500 — largest page size, fewest round trips.
const maxPageSize = 500;

// nextCursor decides the page count, so the only failure left to guard is a
// cursor that never advances.
export const repeatedCursorMessage =
  "getAllRecords returned a repeated pagination cursor, so the record list " +
  "cannot be completed without looping.";

// Same-origin path so it resolves in every env; `signal` is React Query's, so
// cancellation aborts the in-flight page.
const getNrtRecordsPage = async (
  cursor?: string,
  signal?: AbortSignal
): Promise<NrtRecordsResponseBody> => {
  const params = new URLSearchParams({ limit: String(maxPageSize) });
  if (cursor) {
    params.set("cursor", cursor);
  }

  const res = await fetch(
    `${NRT_LAMBDA_BASE_PATH}/getAllRecords?${params.toString()}`,
    { signal }
  );

  if (!res.ok) {
    throw new Error(
      `Failed to fetch NRT records: ${res.status} ${res.statusText}`
    );
  }

  return (await res.json()) as NrtRecordsResponseBody;
};

// No server-side search/filtering, so this loops every page and hands back
// the full list for the page's client-side search/sort/pagination.
export const getAllNrtRecords = async (
  signal?: AbortSignal
): Promise<NrtRecord[]> => {
  const allItems: NrtRecord[] = [];
  const seenCursors = new Set<string>();
  let cursor: string | undefined;

  do {
    // Sequential: each cursor depends on the previous response.
    // eslint-disable-next-line no-await-in-loop
    const { items, pagination } = await getNrtRecordsPage(cursor, signal);
    allItems.push(...items.map(normalizeNrtRecord));
    cursor = pagination.nextCursor ?? undefined;

    if (cursor) {
      if (seenCursors.has(cursor)) {
        throw new Error(repeatedCursorMessage);
      }
      seenCursors.add(cursor);
    }
  } while (cursor);

  return allItems;
};

export function useNrtRecords() {
  return useQuery({
    queryKey: NRT_RECORDS_LIST_QUERY_KEY,
    queryFn: ({ signal }) => getAllNrtRecords(signal),
    retry: 1,
    // refetchOnMount already covers the "fresh on every page load" AC. Focus is
    // excluded because each refetch re-walks every page.
    refetchOnWindowFocus: false,
    staleTime: 0
  });
}

// Reads `{ message }` or `{ errors: [{ field: message }] }`; null if neither shape matches.
export const extractApiErrorMessage = (body: unknown): string | null => {
  const message = (body as { message?: unknown })?.message;
  if (typeof message === "string") return message;

  const errors = (body as { errors?: unknown })?.errors;
  if (Array.isArray(errors)) {
    const messages = errors
      .filter(
        (error): error is Record<string, unknown> =>
          typeof error === "object" && error !== null
      )
      .flatMap(error => Object.values(error))
      .filter((value): value is string => typeof value === "string");
    if (messages.length > 0) return messages.join(", ");
  }
  return null;
};

const extractErrorMessage = async (res: Response): Promise<string> => {
  try {
    const body: unknown = await res.json();
    const message = extractApiErrorMessage(body);
    if (message) return message;
  } catch {
    // Body wasn't JSON (or didn't match either shape) — fall through.
  }
  return `Request failed: ${res.status} ${res.statusText}`;
};

const submitNrtRecord = async (
  path: string,
  method: "POST" | "PUT" | "DELETE",
  body: unknown
): Promise<NrtRecordWire> => {
  const res = await fetch(`${NRT_LAMBDA_BASE_PATH}/${path}`, {
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body)
  });

  if (!res.ok) {
    throw new Error(await extractErrorMessage(res));
  }

  return (await res.json()) as NrtRecordWire;
};

export const createNrtRecord = (
  request: CreateNrtRecordRequest
): Promise<NrtRecord> =>
  submitNrtRecord("createRecord", "POST", request).then(normalizeNrtRecord);

export const updateNrtRecord = (
  request: UpdateNrtRecordRequest
): Promise<NrtRecord> =>
  submitNrtRecord("updateRecord", "PUT", request).then(normalizeNrtRecord);

export const deleteNrtRecord = (
  request: DeleteNrtRecordRequest
): Promise<NrtRecord> =>
  submitNrtRecord("deleteRecord", "DELETE", request).then(normalizeNrtRecord);

// FR: on success the list must auto-refresh — invalidating this query is
// what makes that happen, since RecordsTable renders straight from it.
const useNrtRecordMutation = <TRequest>(
  mutationFn: (request: TRequest) => Promise<NrtRecord>
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NRT_RECORDS_LIST_QUERY_KEY });
    }
  });
};

export function useCreateNrtRecord() {
  return useNrtRecordMutation(createNrtRecord);
}

export function useUpdateNrtRecord() {
  return useNrtRecordMutation(updateNrtRecord);
}

export function useDeleteNrtRecord() {
  return useNrtRecordMutation(deleteNrtRecord);
}
